import React from "react";
import RewardsSummary from "./RewardsSummary";
import MyBadges from "../MyBadges";
import LeaderboardWidget from "../LeaderboardWidget";
import FAQ from "./FAQ";
import { ChevronRight } from "lucide-react";
import "./../../community/_index.scss";
import axios from "axios";
import { URL } from '../../../api/tklEnvironments';
import ComponentContentLoader from "../../../shared-components/page-loader/index-content";

const Rewards: React.FC = () => {
  const [dashboardData, setDashboardData] = React.useState(null);
  const [leaderboardData, setLeaderboardData] = React.useState([]);
  const [userRankInfo, setUserRankInfo] = React.useState(null);
  const [currentUserProfile, setCurrentUserProfile] = React.useState(null);
  const [rewardData, setRewardData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch dashboard data
        const dashboardResponse = await axios.get(`${URL}/rewards/dashboard`);
        console.log("/api/v1/rewards/dashboard data:", dashboardResponse.data);
        setDashboardData(dashboardResponse.data);
        
        // Process leaderboard data from dashboard response
        const data = dashboardResponse.data.leaderboard?.top10 || [];
        console.log("Raw leaderboard data from API:", data);
        const sortedData = data.map((user, index) => ({
          ...user,
          user_name: user.name.split(' ')[0] || user.name,
          user_last_name: user.name.split(' ').slice(1).join(' ') || '',
          rank: user.rank || index + 1
        }));
        console.log("Processed leaderboard data:", sortedData);
        setLeaderboardData(sortedData);

        // Extract user rank info from dashboard response
        const userRankData = dashboardResponse.data.leaderboard?.user || null;
        console.log("User rank data from dashboard:", userRankData);
        setUserRankInfo(userRankData);

        // Extract reward data
        const rewards = dashboardResponse.data.challenges?.rewards || [];
        console.log("Raw rewards data from API:", rewards);
        
        const currentReward = rewards.find(reward => reward.placement === 1) || rewards[0] || null;
        console.log("Selected currentReward (placement 1):", currentReward);
        
        if (currentReward) {
          console.log("🖼️ Reward image details:", {
            image_path: currentReward.image_path,
            name: currentReward.name,
            description: currentReward.description,
            placement: currentReward.placement
          });
        }
        
        const rewardInfo = {
          currentReward,
          userTotalPoints: dashboardResponse.data.challenges?.user_total_points || '0',
          achievementPercent: dashboardResponse.data.achievement_percent_this_month || 0
        };
        console.log("Final reward data being set:", rewardInfo);
        setRewardData(rewardInfo);

        // Fetch current user's profile
        const profileResponse = await axios.get(`${URL}/get-user-bio`);
        console.log("User profile response:", profileResponse.data);
        setCurrentUserProfile(profileResponse.data.response);

      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);



  // Debug log when props change
  React.useEffect(() => {
    console.log("Props being passed to LeaderboardWidget:", {
      leaderboardData,
      userRankInfo,
      currentUserProfile,
      loading
    });
  }, [leaderboardData, userRankInfo, currentUserProfile, loading]);

  if (loading) {
    return <ComponentContentLoader />;
  }

  return (
    <div style={{ position: "relative" }}>
      <main className="pg-main pb-3 pb-md-5 com-page bg-light">
        <div className="max-wrap px-2 px-md-4 mb-3 overflow-hidden community-banner d-md-block">
          <div className="container my-4">
            {/* Back Button */}
            <a
              href="/the-loop"
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 24,
                marginTop: 28,
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'inherit',
                fontSize: 20,
                fontWeight: 700
              }}
            >
              <span style={{ fontSize: 24, fontWeight: 700, marginRight: 12 }}>←</span>
              <span style={{ fontSize: 22, fontWeight: 700 }}>Go back to The Loop</span>
            </a>
            {/* Rewards Section */}
            <h2 className="mb-4">TKL Monthly Leaderboard</h2>
            {/* First row: Ranking and Challenges */}
            <div className="row mb-4">
              <div className="col-lg-5 mb-4" style={{ display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ fontWeight: 'bold', fontStyle: 'italic', marginBottom: 16 }}>My Ranking</h3>
                <LeaderboardWidget 
                  leaderboardData={leaderboardData}
                  userRankInfo={userRankInfo}
                  currentUserProfile={currentUserProfile}
                  loading={false}
                  hideEmails={true}
                />
              </div>
              <div className="col-lg-7 mb-4" style={{ display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ fontWeight: 'bold', fontStyle: 'italic', marginBottom: 16 }}>Monthly Challenges</h3>
                <RewardsSummary 
                  currentReward={rewardData?.currentReward}
                  userTotalPoints={rewardData?.userTotalPoints}
                  achievementPercent={rewardData?.achievementPercent}
                  loading={false}
                />
              </div>
            </div>
            {/* Second row: Badges and FAQ */}
            <div className="row">
              <div className="col-lg-5 mb-4" style={{ display: 'flex', flexDirection: 'column' }}>
                <MyBadges 
                  badges={dashboardData?.badges || []}
                  loading={false}
                />
              </div>
              <div className="col-lg-7 mb-4" style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, justifyContent: 'space-between' }}>
                  <h3 style={{ fontWeight: 'bold', fontStyle: 'italic', margin: 0 }}>FAQ</h3>
                  <a
                    href="/faq"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontWeight: 600,
                      fontSize: 20,
                      fontStyle: 'italic',
                      textDecoration: 'none',
                      cursor: "pointer"
                    }}
                  >
                    View More
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      background: '#fff',
                      border: '2px solid #18998E',
                      borderRadius: '50%',
                      marginLeft: 4
                    }}>
                      <ChevronRight style={{ width: 20, height: 20, color: "#18998E" }} />
                    </span>
                  </a>
                </div>
                <FAQ />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Rewards;